<template>
    <v-container fluid>
        <v-row class="align-center">
            <!-- Breadcrumbs -->
            <v-breadcrumbs
                :items="[
                    { text: 'Home', disable: false, href: '/#/' },
                    { text: 'Orders', disable: false, href: '/#/orders' },
                    { text: salesOrderNo, disabled: true }
                ]"
                divider="/"
            ></v-breadcrumbs>
            <v-spacer></v-spacer>
            <v-switch
                v-model="isSalesOrderPrinted"
                :loading="isSavingOrderPrintStatus"
                label="Printed"
                color="success"
                class="mr-4"
            ></v-switch>
            <v-btn
                v-if="orderCarts.length > 0"
                rounded
                outlined
                small
                color="success"
                class="mr-4"
                @click="printAllOrderCarts"
                >Print All</v-btn
            >
            <v-btn
                rounded
                outlined
                small
                color="primary"
                class="mr-6"
                v-if="
                    hasPermission('change_salesorder') &&
                        (salesOrder.orderStatus == 'PENDING' ||
                            salesOrder.orderStatus == 'READY_TO_PICK')
                "
                :loading="isCompletingSalesOrder"
                @click="completeOrder(salesOrder)"
                >Complete</v-btn
            >
        </v-row>

        <!-- product dialog -->
        <v-dialog v-model="productDialog" scrollable>
            <v-card tile>
                <v-toolbar flat dark color="primary">
                    <v-btn icon dark @click="closeProductDialog">
                        <v-icon>close</v-icon>
                    </v-btn>
                    <v-toolbar-title>Product</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        dark
                        @click="goToProductDetail(selectedProductNodeId)"
                    >
                        <v-icon>launch</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text class="mx-0 px-0">
                    <ProductDetailComponent
                        v-if="selectedProductNodeId"
                        :productNodeId="selectedProductNodeId"
                        v-on:product-items-updated="refetchOrderDetail"
                    ></ProductDetailComponent>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Order Plant Growth Task List -->
        <v-dialog v-model="salesOrderPlantGrowthTaskListDialog">
            <v-card tile>
                <v-toolbar flat dark color="primary">
                    <v-toolbar-title>
                        <v-icon class="mx-2">spa</v-icon>Assigned
                        Plants</v-toolbar-title
                    >
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        dark
                        @click="closeSalesOrderPlantGrowthTaskListDialog"
                    >
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-card-text class="mx-0 px-0">
                    <OrderPlantGrowthTaskList
                        v-if="selectedSalesOrderPlantNodeId"
                        :salesOrderPlantNodeId="selectedSalesOrderPlantNodeId"
                    ></OrderPlantGrowthTaskList>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- order product editor -->
        <v-dialog v-model="orderProductEditorDialog" scrollable>
            <v-card tile>
                <v-toolbar flat dark color="primary">
                    <v-btn
                        icon
                        dark
                        @click="closeSalesOrderProductEditorDialog"
                    >
                        <v-icon>close</v-icon>
                    </v-btn>
                    <v-toolbar-title>
                        {{
                            selectedSalesOrderProduct.product
                                ? selectedSalesOrderProduct.product.name
                                : ""
                        }}
                        <v-icon class="ml-2">lock</v-icon>
                    </v-toolbar-title>
                    <v-spacer></v-spacer>
                    <div class="headline mr-2" style="white-space: nowrap">
                        {{
                            selectedSalesOrderProduct.quantity /
                                selectedSalesOrderProduct.quantityPerContainer
                        }}
                        x {{ selectedSalesOrderProduct.quantityPerContainer }}
                    </div>
                    <v-spacer></v-spacer>
                    <v-icon class="mr-1">eco</v-icon>
                    <div class="headline mr-2">
                        {{ selectedSalesOrderProduct.quantity }}
                    </div>
                </v-toolbar>
                <OrderProductEditor
                    v-if="selectedSalesOrderProduct.id"
                    :salesOrderProductNodeId="selectedSalesOrderProduct.id"
                    :customer="salesOrder.customer"
                    v-on:cancelled="closeSalesOrderProductEditorDialog"
                    v-on:saved="onSalesOrderProductEditorDialogSaved"
                ></OrderProductEditor>
            </v-card>
        </v-dialog>

        <!-- order cart preview dialog -->
        <v-dialog v-model="orderCartPreviewListDialog" scrollable>
            <v-card tile>
                <v-toolbar flat dark color="primary">
                    <v-btn icon dark @click="closeOrderCartPreviewListDialog">
                        <v-icon>close</v-icon>
                    </v-btn>
                    <v-toolbar-title>Order Carts</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn
                        small
                        rounded
                        outlined
                        class="mr-3"
                        :loading="isLoadingOrderCartPreview"
                        v-print="'#printMe'"
                        @click="updateSalesOrderPrintStatus(true)"
                        >Print</v-btn
                    >
                </v-toolbar>

                <v-card-text class="mx-0 px-0">
                    <OrderCartPreviewList
                        v-if="selectedOrderCartNodeIdList.length > 0"
                        :salesOrderCartNodeIdList="selectedOrderCartNodeIdList"
                        :loading.sync="isLoadingOrderCartPreview"
                    ></OrderCartPreviewList>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- loading status -->
        <v-row>
            <v-progress-linear
                v-if="$apollo.queries.salesOrder.loading"
                color="primary"
                indeterminate
                rounded
                class="mt-0"
            ></v-progress-linear>
        </v-row>

        <!-- Order header -->
        <v-row class="mx-1">
            <v-col cols="12" sm="6" md="4" class="d-flex flex-column px-0">
                <v-card flat>
                    <v-list-item>
                        <v-list-item-icon>
                            <v-icon>receipt</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>{{
                                salesOrderNo
                            }}</v-list-item-title>
                            <v-list-item-subtitle>
                                Order
                                <span
                                    v-if="
                                        salesOrder &&
                                            salesOrder.externalLastSyncTime
                                    "
                                    class="ml-1"
                                    >(last sync
                                    {{
                                        salesOrder.externalLastSyncTime
                                            | moment("from", new Date())
                                    }})</span
                                >
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                        <v-list-item-icon>
                            <v-icon>assignment_turned_in</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>
                                <v-chip
                                    v-if="
                                        salesOrder.orderStatus ==
                                            `READY_TO_PICK`
                                    "
                                    small
                                    color="success"
                                    >{{
                                        salesOrder ? salesOrder.orderStatus : ""
                                    }}</v-chip
                                >
                                <v-chip
                                    v-else-if="
                                        salesOrder.orderStatus == `PENDING`
                                    "
                                    small
                                    color="warning"
                                    >{{
                                        salesOrder ? salesOrder.orderStatus : ""
                                    }}</v-chip
                                >
                                <v-chip v-else small>{{
                                    salesOrder ? salesOrder.orderStatus : ""
                                }}</v-chip>
                                <span
                                    v-if="externalOrderStatusText"
                                    class="ml-1"
                                    >/ {{ externalOrderStatusText }}</span
                                >
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="salesOrder.closedBy">Closed by {{
                                salesOrder.closedBy
                            }}</v-list-item-subtitle>
                            <v-list-item-subtitle>{{
                                statusDate | moment("from", new Date())
                            }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                        <v-list-item-icon>
                            <v-icon>post_add</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>{{
                                createdDate
                                    | moment("utc", "MMMM Do YYYY, HH:mm")
                            }}</v-list-item-title>
                            <v-list-item-subtitle
                                >Created At</v-list-item-subtitle
                            >
                        </v-list-item-content>
                    </v-list-item>
                    <!--                     
                    <v-list-item>
                        <v-list-item-icon>
                            <v-icon>textsms</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-row class="px-3 align-start">
                                <v-text-field
                                    outlined
                                    v-model="salesOrder.note"
                                    label="Note"
                                    :disabled="!isEditingOrderNote"
                                ></v-text-field>
                                <v-btn
                                    v-if="!isEditingOrderNote"
                                    text
                                    small
                                    color="grey darken-2"
                                    @click="isEditingOrderNote = !isEditingOrderNote"
                                >
                                    <v-icon>edit</v-icon>
                                </v-btn>
                                <v-btn
                                    v-else
                                    text
                                    small
                                    color="primary"
                                    :loading="isSavingOrderNote"
                                    @click="saveOrderNote(salesOrder.note)"
                                >
                                    <v-icon>save</v-icon>
                                </v-btn>
                            </v-row>
                        </v-list-item-content>
                    </v-list-item>-->
                </v-card>
            </v-col>

            <v-col cols="12" sm="6" md="4" class="d-flex flex-column px-0">
                <v-card flat>
                    <v-list-item>
                        <v-list-item-icon>
                            <v-icon>person</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>{{
                                customer.name
                            }}</v-list-item-title>
                            <v-list-item-subtitle
                                >Customer</v-list-item-subtitle
                            >
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                        <v-list-item-icon>
                            <v-icon>local_shipping</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>{{
                                (salesOrder.orderLoadDate ||
                                    salesOrder.deliverDatePlanned)
                                    | moment("utc", "dd, DD MMMM YYYY, HH:mm")
                            }}</v-list-item-title>
                            <v-list-item-subtitle
                                >Delivery Date</v-list-item-subtitle
                            >
                        </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                        <v-list-item-icon>
                            <v-icon>spa</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>{{
                                salesOrder ? salesOrder.totalProductQuantity : 0
                            }}</v-list-item-title>
                            <v-list-item-subtitle
                                >Quantity</v-list-item-subtitle
                            >
                        </v-list-item-content>
                    </v-list-item>
                </v-card>
            </v-col>

            <v-col cols="12" md="4" class="d-flex flex-column px-0">
                <v-card flat>
                    <v-list-item
                        v-for="(orderCart, index) in orderCarts"
                        :key="index"
                    >
                        <v-list-item-icon>
                            <v-icon>shopping_cart</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>{{
                                orderCart.node.externalSalesOrderCartId
                            }}</v-list-item-title>
                            <v-list-item-subtitle
                                >Kar {{ orderCart.node.cartSeq }} /
                                {{
                                    salesOrder.salesOrderCartSet.totalCount
                                }}</v-list-item-subtitle
                            >
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn
                                :outlined="
                                    !orderCart.node.isReady ||
                                        salesOrder.externalOrderStatus === '3'
                                "
                                rounded
                                small
                                class="ml-2"
                                :color="
                                    orderCart.node.isReady &&
                                    salesOrder.externalOrderStatus !== '3'
                                        ? 'success'
                                        : 'grey'
                                "
                                @click="
                                    openOrderCartPreviewListDialog(
                                        orderCart.node.id
                                    )
                                "
                                >Print</v-btn
                            >
                        </v-list-item-action>
                    </v-list-item>

                    <v-list-item
                        v-if="
                            !salesOrder.salesOrderCartSet ||
                                salesOrder.salesOrderCartSet.edges.length == 0
                        "
                    >
                        <v-list-item-icon>
                            <v-icon>shopping_cart</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>
                            <v-list-item-title>No cart data</v-list-item-title>
                            <v-list-item-subtitle>Kar</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </v-card>
            </v-col>

            <!-- <v-col cols="12" md="4" lg="4" class="d-flex flex-column">
                <v-list-item>
                    <v-list-item-icon>
                        <v-icon>comment</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                        <v-list-item-title>Note</v-list-item-title>
                        <v-list-item-subtitle></v-list-item-subtitle>
                    </v-list-item-content>
                    <v-btn icon small>
                        <v-icon>edit</v-icon>
                    </v-btn>
                </v-list-item>
                <v-textarea
                    outlined
                    label="-"
                    rows="5"
                    hide-details
                    class="px-5"
                    :value="salesOrder ? salesOrder.note : null"
                ></v-textarea>
            </v-col>-->
        </v-row>

        <!-- Item divider -->
        <v-row>
            <v-card-text class="font-weight-medium">
                <v-row align="center">
                    <v-divider></v-divider>
                    <span class="pl-3 pr-3">Order Items</span>
                    <v-divider></v-divider>
                </v-row>
            </v-card-text>
        </v-row>

        <!-- order items -->
        <div v-if="orderItems.length == 0">
            <p
                v-if="$apollo.queries.salesOrder.loading"
                class="text-center grey--text"
            >
                Loading...
            </p>
            <p v-else class="text-center grey--text">- no data -</p>
        </div>

        <v-row
            v-else
            class="mx-1"
            v-for="(orderItem, index) in orderItems"
            :key="index"
        >
            <v-col class="12">
                <v-card
                    width="100%"
                    outlined
                    :loading="$apollo.queries.salesOrder.loading"
                >
                    <!-- product title -->
                    <v-card-title class="subtitle-1 grey lighten-3 px-0">
                        <!-- Product title and subtitle -->
                        <v-col cols="12" md="5" class="px-0">
                            <v-list-item two-line class="px-1">
                                <v-list-item-avatar class="mx-1">
                                    <!-- product ready icon -->
                                    <v-icon
                                        v-if="orderItem.node.isReady"
                                        class="mr-1 success--text"
                                        >check_circle</v-icon
                                    >
                                    <!-- product plants not configured icon -->
                                    <v-icon
                                        v-else-if="
                                            !orderItem.node.product
                                                .isPlantsConfigured
                                        "
                                        class="mr-1 error--text"
                                        >settings</v-icon
                                    >
                                    <!-- order product plant not confirmed icon -->
                                    <!-- <v-icon
                                        v-else-if="orderItem.node.product.isDynamic && orderItem.node.salesOrderPlantSet.edges.length == 0"
                                        class="mr-1 error--text"
                                    >warning</v-icon>-->
                                    <v-icon
                                        v-else-if="
                                            !orderItem.node.isReady &&
                                                orderItem.node.isManualConfig
                                        "
                                        class="mr-1 grey--text"
                                        >hourglass_full</v-icon
                                    >
                                    <v-icon v-else class="mr-1 grey--text"
                                        >error</v-icon
                                    >
                                </v-list-item-avatar>

                                <v-list-item-content>
                                    <!-- title -->
                                    <v-list-item-title class="wrap-text">
                                        {{ orderItem.node.product.name }}
                                        <!-- Dynamic chip -->
                                        <v-chip
                                            v-if="
                                                orderItem.node.product.isDynamic
                                            "
                                            small
                                            color="primary"
                                            class="ml-2"
                                            >Dynamic</v-chip
                                        >
                                        <v-btn
                                            text
                                            icon
                                            @click="
                                                openProductDialog(
                                                    orderItem.node.product
                                                )
                                            "
                                        >
                                            <v-icon
                                                >keyboard_arrow_right</v-icon
                                            >
                                        </v-btn>
                                    </v-list-item-title>

                                    <!-- if dynamic product, show #plants and #colors -->
                                    <v-list-item-subtitle
                                        v-if="orderItem.node.product.isDynamic"
                                    >
                                        <span class="primary--text">
                                            <strong
                                                >{{
                                                    orderItem.node.product
                                                        .numMixPlantsPerUnitMin
                                                }}-{{
                                                    orderItem.node.product
                                                        .numMixPlantsPerUnitMax
                                                }}</strong
                                            >
                                            plants
                                        </span>
                                        <span
                                            v-if="
                                                orderItem.node.product
                                                    .numColorsPerUnitMax
                                            "
                                            class="primary--text"
                                        >
                                            ,
                                            <strong
                                                >{{
                                                    orderItem.node.product
                                                        .numColorsPerUnitMin
                                                }}-{{
                                                    orderItem.node.product
                                                        .numColorsPerUnitMax
                                                }}</strong
                                            >
                                            colors
                                        </span>
                                    </v-list-item-subtitle>

                                    <!-- order product note -->
                                    <v-list-item-subtitle
                                        v-if="
                                            hasPermission('change_salesorder')
                                        "
                                    >
                                        <v-row
                                            class="pl-1 mr-2 mt-3 mb-1 align-start"
                                            dense
                                        >
                                            <v-text-field
                                                outlined
                                                dense
                                                hide-details
                                                v-model="orderItem.node.note"
                                                label="Note"
                                                @keydown.enter="
                                                    saveOrderProductNote(
                                                        orderItem.node
                                                    )
                                                "
                                            ></v-text-field>
                                            <v-btn
                                                text
                                                small
                                                fab
                                                color="primary"
                                                :loading="
                                                    orderItem.node
                                                        .isSavingOrderProductNote
                                                "
                                                @click="
                                                    saveOrderProductNote(
                                                        orderItem.node
                                                    )
                                                "
                                            >
                                                <v-icon>save</v-icon>
                                            </v-btn>
                                        </v-row>
                                    </v-list-item-subtitle>

                                    <!-- subtitle -->
                                    <v-list-item-subtitle
                                        v-if="
                                            !orderItem.node.product
                                                .isPlantsConfigured
                                        "
                                        class="error--text"
                                    >
                                        <v-btn
                                            v-if="
                                                hasPermission(
                                                    'change_product'
                                                ) &&
                                                    !orderItem.node.product
                                                        .isPlantsConfigured
                                            "
                                            small
                                            rounded
                                            outlined
                                            class="mt-1 error--text"
                                            @click="
                                                openProductDialog(
                                                    orderItem.node.product
                                                )
                                            "
                                            >Configure Product</v-btn
                                        >
                                    </v-list-item-subtitle>

                                    <!-- <v-list-item-subtitle
                                        v-else-if="orderItem.node.product.isDynamic && (!orderItem.node.isPlantsConfirmed || orderItem.node.salesOrderPlantSet.edges.length == 0)"
                                        class="error--text"
                                    >
                                        Select plants
                                        <v-btn
                                            small
                                            rounded
                                            outlined
                                            class="ml-1 primary--text"
                                            v-if="orderItem.node.isEditing"
                                            @click="onCancelEditOrderItemPlants(orderItem)"
                                        >Cancel</v-btn>
                                        <v-btn
                                            small
                                            rounded
                                            outlined
                                            class="ml-1 error--text"
                                            :disabled="countSalesOrderItemSelectedPlants(orderItem)==0"
                                            :loading="isSavingProductPlants"
                                            @click="confirmProductPlants(orderItem)"
                                        >Confirm</v-btn>
                                    </v-list-item-subtitle>-->
                                    <!-- <v-list-item-subtitle
                                        v-else-if="orderItem.node.product.isDynamic && orderItem.node.isPlantsConfirmed"
                                        class="grey--text"
                                    >
                                        Processing
                                        <v-btn
                                            small
                                            rounded
                                            outlined
                                            class="ml-1 error--text"
                                            @click="onEditOrderItemPlants(orderItem)"
                                        >Edit</v-btn>
                                    </v-list-item-subtitle>-->
                                    <v-list-item-subtitle
                                        v-else-if="orderItem.node.isReady"
                                        class="success--text"
                                    >
                                        <!-- <v-btn
                                            v-if="orderItem.node.product.isDynamic"
                                            small
                                            rounded
                                            outlined
                                            class="ml-1 error--text"
                                            @click="onEditOrderItemPlants(orderItem)"
                                        >Edit</v-btn>-->
                                    </v-list-item-subtitle>
                                    <!-- <v-list-item-subtitle v-else class="grey--text">Processing</v-list-item-subtitle> -->
                                </v-list-item-content>
                            </v-list-item>
                        </v-col>

                        <!-- Product quantity and labels -->
                        <v-col cols="12" md="7">
                            <v-card>
                                <v-row dense>
                                    <v-col>
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-title
                                                    >Quantity</v-list-item-title
                                                >
                                                <v-list-item-subtitle
                                                    >{{
                                                        orderItem.node
                                                            .quantity /
                                                            orderItem.node
                                                                .quantityPerContainer
                                                    }}
                                                    x
                                                    {{
                                                        orderItem.node
                                                            .quantityPerContainer
                                                    }}</v-list-item-subtitle
                                                >
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col>
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-title
                                                    >Pot</v-list-item-title
                                                >
                                                <v-list-item-subtitle>{{
                                                    orderItem.node.product
                                                        .externalPotsize
                                                }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col>
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-title
                                                    >HG</v-list-item-title
                                                >
                                                <v-list-item-subtitle>{{
                                                    orderItem.node.product
                                                        .externalHeight
                                                }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col>
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-title
                                                    >Fust</v-list-item-title
                                                >
                                                <v-list-item-subtitle>{{
                                                    orderItem.node
                                                        .externalContainer
                                                }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col>
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-title
                                                    >ET</v-list-item-title
                                                >
                                                <v-list-item-subtitle>{{
                                                    orderItem.node.product
                                                        .externalLabel
                                                }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col>
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-title
                                                    >HS</v-list-item-title
                                                >
                                                <v-list-item-subtitle>{{
                                                    orderItem.node.product
                                                        .externalCover
                                                }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                    <v-col>
                                        <v-list-item two-line>
                                            <v-list-item-content>
                                                <v-list-item-title
                                                    >ST</v-list-item-title
                                                >
                                                <v-list-item-subtitle>{{
                                                    orderItem.node.product
                                                        .externalSticker
                                                }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                        </v-list-item>
                                    </v-col>
                                </v-row>
                            </v-card>
                            <v-row v-if="hasPermission('change_salesorder')">
                                <v-col>
                                    <v-btn
                                        outlined
                                        rounded
                                        small
                                        :disabled="
                                            $apollo.queries.salesOrder.loading
                                        "
                                        @click="
                                            openSalesOrderProductEditorDialog(
                                                orderItem.node
                                            )
                                        "
                                        >Manually select plants</v-btn
                                    >
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-card-title>

                    <!-- if product plant is not configured -->
                    <div
                        v-if="!orderItem.node.product.isPlantsConfigured"
                    ></div>

                    <!-- dynamic product -->
                    <v-card v-else-if="orderItem.node.product.isDynamic">
                        <!-- if dynammic product plants are NOT ready -->
                        <v-simple-table class="px-5">
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <!-- <th
                                            class="text-left"
                                            v-if="!orderItem.node.isPlantsConfirmed || orderItem.node.salesOrderPlantSet.edges.length == 0"
                                        >Select</th>-->
                                        <th class="text-left">Priority</th>
                                        <th class="text-left">Plant</th>
                                        <th class="text-left">Pot Size</th>
                                        <th class="text-left">Color</th>
                                        <th class="text-left">Environment</th>
                                        <th class="text-left">Tag</th>
                                        <!-- <th class="text-left">Required</th> -->
                                        <th class="text-left">Inventory</th>
                                        <th class="text-left">Assigned</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr
                                        v-for="productItem in sortProductItems(
                                            orderItem.node.product
                                                .productItemSet
                                        )"
                                        :key="productItem.node.id"
                                        :set="
                                            (plantQuantity = findAssignedPlantsCount(
                                                orderItem.node
                                                    .salesOrderPlantSet,
                                                productItem.node.id
                                            ))
                                        "
                                    >
                                        <!-- <td
                                            v-if="!orderItem.node.isPlantsConfirmed || orderItem.node.salesOrderPlantSet.edges.length == 0"
                                        >
                                            <v-checkbox v-model="productItem.node.selected"></v-checkbox>
                                        </td>-->
                                        <td>{{ productItem.node.priority }}</td>
                                        <td>
                                            <!-- <v-icon
                                                v-if="findAssignedPlantsStatus(orderItem.node.salesOrderPlantSet, productItem.node.id)"
                                                small
                                                class="success--text mr-1"
                                            >fiber_manual_record</v-icon>
                                            <v-icon
                                                v-else
                                                small
                                                class="grey--text text--lighten-2 mr-1"
                                            >fiber_manual_record</v-icon>-->
                                            {{ productItem.node.plant.name }}
                                        </td>
                                        <td>
                                            {{
                                                productItem.node.potSize
                                                    ? productItem.node.potSize
                                                          .potSizeCm
                                                    : "-"
                                            }}
                                            cm
                                        </td>
                                        <td>
                                            {{
                                                productItem.node.color
                                                    ? productItem.node.color
                                                          .color
                                                    : "-"
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                productItem.node.environment
                                                    ? productItem.node
                                                          .environment
                                                          .environment
                                                    : "-"
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                productItem.node.tag
                                                    ? productItem.node.tag.tag
                                                    : "-"
                                            }}
                                        </td>
                                        <!-- <td>{{ orderItem.node.quantity / orderItem.node.product.numMixPlantsPerUnitMin * productItem.node.numPlants | round(1)}}</td> -->

                                        <td
                                            :set="
                                                ((plantInventory =
                                                    productItem.node
                                                        .availableSalesInventory),
                                                (lockedInventory =
                                                    productItem.node
                                                        .lockedSalesInventory))
                                            "
                                        >
                                            <span
                                                v-if="plantInventory == 0"
                                                class="error--text"
                                                >{{ plantInventory }}</span
                                            >
                                            <span v-else>{{
                                                plantInventory
                                            }}</span>
                                            <span
                                                v-if="lockedInventory > 0"
                                                class="caption error--text"
                                            >
                                                ({{
                                                    lockedInventory
                                                }}
                                                locked)</span
                                            >
                                        </td>
                                        <td>
                                            <span
                                                v-if="plantQuantity === 0"
                                                class="error--text"
                                                >{{ plantQuantity }}</span
                                            >
                                            <span
                                                v-else-if="
                                                    !orderItem.node.isReady &&
                                                        orderItem.node
                                                            .isManualConfig
                                                "
                                                class="grey--text"
                                                >{{ plantQuantity }}</span
                                            >
                                            <span v-else>{{
                                                plantQuantity
                                            }}</span>
                                            <v-icon
                                                small
                                                class="ml-2"
                                                color="grey lighten-1"
                                                v-if="
                                                    orderItem.node
                                                        .isManualConfig
                                                "
                                                >lock</v-icon
                                            >
                                            <v-btn
                                                v-if="plantQuantity > 0"
                                                small
                                                icon
                                                @click="
                                                    openSalesOrderPlantGrowthTaskList(
                                                        orderItem.node
                                                            .salesOrderPlantSet,
                                                        productItem.node.id
                                                    )
                                                "
                                            >
                                                <v-icon small
                                                    >remove_red_eye</v-icon
                                                >
                                            </v-btn>
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card>

                    <!-- else non-dynamic products, -->
                    <v-card v-else>
                        <v-simple-table class="px-5">
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-left">Plant</th>
                                        <th class="text-left">Pot Size</th>
                                        <th class="text-left">Color</th>
                                        <th class="text-left">Environment</th>
                                        <th class="text-left">Tag</th>
                                        <!-- <th class="text-left">Required</th> -->
                                        <th class="text-left">Inventory</th>
                                        <th class="text-left">Assigned</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        v-for="productItem in orderItem.node
                                            .product.productItemSet.edges"
                                        :key="productItem.node.id"
                                        :set="
                                            (plantQuantity = findAssignedPlantsCount(
                                                orderItem.node
                                                    .salesOrderPlantSet,
                                                productItem.node.id
                                            ))
                                        "
                                    >
                                        <td>
                                            <!-- <v-icon
                                                v-if="plantQuantity"
                                                small
                                                class="success--text mr-1"
                                            >fiber_manual_record</v-icon>
                                            <v-icon
                                                v-else
                                                small
                                                class="grey--text text--lighten-2 mr-1"
                                            >fiber_manual_record</v-icon>-->
                                            {{ productItem.node.plant.name }}
                                        </td>
                                        <td>
                                            {{
                                                productItem.node.potSize
                                                    ? productItem.node.potSize
                                                          .potSizeCm
                                                    : "-"
                                            }}
                                            cm
                                        </td>
                                        <td>
                                            {{
                                                productItem.node.color
                                                    ? productItem.node.color
                                                          .color
                                                    : "-"
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                productItem.node.environment
                                                    ? productItem.node
                                                          .environment
                                                          .environment
                                                    : "-"
                                            }}
                                        </td>
                                        <td>
                                            {{
                                                productItem.node.tag
                                                    ? productItem.node.tag.tag
                                                    : "-"
                                            }}
                                        </td>
                                        <!-- <td>{{ orderItem.node.quantity / calculateStaticProductNumPlants(orderItem.node.product.productItemSet) * productItem.node.numPlants | round(1) }}</td> -->

                                        <td
                                            :set="
                                                (plantInventory =
                                                    productItem.node
                                                        .availableSalesInventory)
                                            "
                                        >
                                            <span
                                                v-if="plantInventory == 0"
                                                class="error--text"
                                                >{{ plantInventory }}</span
                                            >
                                            <span v-else>{{
                                                plantInventory
                                            }}</span>
                                        </td>
                                        <td>
                                            <span
                                                v-if="plantQuantity === 0"
                                                class="error--text"
                                                >{{ plantQuantity }}</span
                                            >
                                            <span
                                                v-else-if="
                                                    !orderItem.node.isReady &&
                                                        orderItem.node
                                                            .isManualConfig
                                                "
                                                class="grey--text"
                                                >{{ plantQuantity }}</span
                                            >
                                            <span v-else>{{
                                                plantQuantity
                                            }}</span>
                                            <v-icon
                                                small
                                                class="ml-2"
                                                v-if="
                                                    orderItem.node
                                                        .isManualConfig
                                                "
                                                >lock</v-icon
                                            >
                                            <v-btn
                                                v-if="plantQuantity > 0"
                                                small
                                                icon
                                                @click="
                                                    openSalesOrderPlantGrowthTaskList(
                                                        orderItem.node
                                                            .salesOrderPlantSet,
                                                        productItem.node.id
                                                    )
                                                "
                                            >
                                                <v-icon small
                                                    >remove_red_eye</v-icon
                                                >
                                            </v-btn>
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import ProductDetailComponent from "@/components/products/ProductDetailComponent.vue";
// import OrderCartPreview from "@/components/sales/OrderCartPreview.vue";
import OrderCartPreviewList from "@/components/sales/OrderCartPreviewList.vue";
import OrderPlantGrowthTaskList from "@/components/sales/OrderPlantGrowthTaskList.vue";
import OrderProductEditor from "@/components/sales/OrderProductEditor.vue";

export default {
    name: "order-detail-page",
    components: {
        ProductDetailComponent,
        OrderCartPreviewList,
        OrderPlantGrowthTaskList,
        OrderProductEditor
    },

    apollo: {
        salesOrder: {
            query: gql`
                query salesOrder($id: ID!) {
                    salesOrder(id: $id) {
                        id
                        note
                        customer {
                            id
                            name
                        }
                        orderCreatedDate
                        externalOrderStatus
                        externalLastSyncTime
                        orderStatus
                        orderStatusDate
                        isPrinted
                        salesOrderNo
                        orderLoadDate
                        deliverDatePlanned
                        totalProductQuantity
                        closedDate
                        closedBy
                        salesOrderCartSet {
                            totalCount
                            edges {
                                node {
                                    id
                                    cartSeq
                                    isReady
                                    externalSalesOrderCartId
                                }
                            }
                        }
                        salesOrderProductSet {
                            edges {
                                node {
                                    id
                                    note
                                    quantity
                                    quantityPerContainer
                                    externalContainer
                                    isPlantsConfirmed
                                    isManualConfig
                                    isReady
                                    salesOrderPlantSet {
                                        totalCount
                                        edges {
                                            node {
                                                id
                                                productItem {
                                                    id
                                                }
                                                quantity
                                                numPlantsPerProduct
                                                isReady
                                                plant {
                                                    id
                                                    name
                                                }
                                                potSize {
                                                    id
                                                    potSizeCm
                                                }
                                                color {
                                                    id
                                                    color
                                                }
                                                environment {
                                                    id
                                                    environment
                                                }
                                                tag {
                                                    id
                                                    tag
                                                }
                                            }
                                        }
                                    }
                                    product {
                                        id
                                        isDynamic
                                        name
                                        externalCode
                                        externalPotsize
                                        externalHeight
                                        externalCover
                                        externalLabel
                                        externalSticker
                                        isPlantsConfigured
                                        numColorsPerUnitMax
                                        numColorsPerUnitMin
                                        numMixPlantsPerUnitMin
                                        numMixPlantsPerUnitMax
                                        productItemSet {
                                            totalCount
                                            edges {
                                                node {
                                                    id
                                                    priority
                                                    numPlants
                                                    plant {
                                                        id
                                                        name
                                                    }
                                                    potSize {
                                                        id
                                                        potSizeCm
                                                    }
                                                    color {
                                                        id
                                                        color
                                                    }
                                                    tag {
                                                        id
                                                        tag
                                                    }
                                                    environment {
                                                        id
                                                        environment
                                                    }
                                                    availableSalesInventory
                                                    lockedSalesInventory
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            `,
            variables() {
                return {
                    id: this.$route.params.id
                };
            },
            // Additional options here
            fetchPolicy: "cache-and-network",
            update(data) {
                this.isSalesOrderPrinted = data.salesOrder.isPrinted;

                //return result to current componet
                return data.salesOrder;
            }
            // pollInterval: 60000 // ms
        }
    },

    data() {
        return {
            // isLoadingOrder: false,

            isSavingOrderPrintStatus: false,
            isSavingProductPlants: false,
            isCompletingSalesOrder: false,
            isCancellingSalesOrder: false,
            isLoadingOrderCartPreview: false,
            isEditingOrderNote: false,
            isSavingOrderNote: false,
            isSalesOrderPrinted: false,

            productDialog: false,
            // orderCartPreviewDialog: false,
            orderCartPreviewListDialog: false,
            orderProductEditorDialog: false,
            salesOrderPlantGrowthTaskListDialog: false,

            selectedDynamicProductPlants: {},
            selectedSalesOrderPlantNodeId: null,

            selectedProductNodeId: null,
            // selectedOrderCartNodeId: null,
            selectedOrderCartNodeIdList: [],
            selectedSalesOrderProduct: {},

            // orderNodeId: this.$route.params.id,
            salesOrder: {}
        };
    },
    computed: {
        // orderNodeId() {
        //     return this.$route.params.id;
        // },
        salesOrderNo() {
            return this.salesOrder ? this.salesOrder.salesOrderNo : "";
        },
        externalOrderStatusText() {
            if (!this.salesOrder) {
                return null;
            }

            // console.log(this.salesOrder);

            switch (this.salesOrder.externalOrderStatus) {
                case "1":
                    return "OPEN";
                case "2":
                    return "DELIVERED";
                case "3":
                    return "CANCELLED";
                default:
                    return this.salesOrder.externalOrderStatus;
            }
        },
        createdDate() {
            return this.salesOrder ? this.salesOrder.orderCreatedDate : null;
        },
        statusDate() {
            return this.salesOrder ? this.salesOrder.orderStatusDate : null;
        },
        deliveryDate() {
            return this.salesOrder ? this.salesOrder.deliverDatePlanned : null;
        },
        customer() {
            return this.salesOrder && this.salesOrder.customer
                ? this.salesOrder.customer
                : {};
        },
        orderItems() {
            return this.salesOrder && this.salesOrder.salesOrderProductSet
                ? this.salesOrder.salesOrderProductSet.edges
                : [];
        },
        orderCarts() {
            if (this.salesOrder.salesOrderCartSet) {
                var _orderCarts = this.salesOrder.salesOrderCartSet.edges;

                return _orderCarts.sort((a, b) => {
                    if (a.node.cartSeq > b.node.cartSeq) {
                        return 1;
                    }
                    if (a.node.cartSeq < b.node.cartSeq) {
                        return -1;
                    }
                    return 0;
                });
            } else {
                return [];
            }

            // return this.salesOrder.salesOrderCartSet ? this.salesOrder.salesOrderCartSet.edges.sort((a, b) => {
            //           if (a.node.cartSeq > b.node.cartSeq) {
            //               return 1;
            //           }
            //           if (a.node.cartSeq < b.node.cartSeq) {
            //               return -1;
            //           }
            //           return 0;
            //       })
            //     : [];
        },
        salesOrderCancellable() {
            if (
                this.salesOrder.orderStatus == "PENDING" ||
                this.salesOrder.orderStatus == "READY_TO_PICK"
            ) {
                return true;
            } else {
                return false;
            }
        },
        salesOrderCompletable() {
            return this.salesOrder.orderStatus == "READY_TO_PICK";
        }
    },
    watch: {
        isSalesOrderPrinted(val) {
            if (val != this.salesOrder.isPrinted) {
                this.updateSalesOrderPrintStatus(val);
            }
        }
    },
    created() {
        // this.initialize();
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        initialize() {
            // this.fetchSingleOrder();
            // this.fetchGreenhouseDepartmentSections();
        },

        // fetchSingleOrder() {
        //     this.isLoadingOrder = true;

        //     this.$store
        //         .dispatch("sales/fetchSingleSalesOrder", this.$route.params.id)
        //         .then(response => {
        //             console.log(response);

        //             this.order = response;

        //             this.isLoadingOrder = false;
        //         })
        //         .catch(error => {
        //             /*handle errors*/
        //             console.log(error);
        //             this.isLoadingOrder = false;
        //         })
        //         .finally();
        // }

        calculatePlantSalesInventory(tenantGrowthTasks) {
            var sum = 0;

            var orderCustomer = this.salesOrder.customer;

            tenantGrowthTasks.edges.forEach(e => {
                if (!e.node.lockedForCustomer) {
                    sum += e.node.totalInventory;
                } else if (
                    orderCustomer &&
                    e.node.lockedForCustomer.id == orderCustomer.id
                ) {
                    sum += e.node.totalInventory;
                }
            });

            return sum;
        },

        findAssignedPlantsStatus(salesOrderPlantSet, productItemNodeId) {
            let _assignedPlant = salesOrderPlantSet.edges.find(item => {
                return item.node.productItem.id == productItemNodeId;
            });

            return _assignedPlant ? _assignedPlant.node.isReady : false;
        },

        findAssignedPlantsCount(salesOrderPlantSet, productItemNodeId) {
            // console.log(salesOrderPlantSet, productItemNodeId)

            let _assignedPlant = salesOrderPlantSet.edges.find(item => {
                return item.node.productItem.id == productItemNodeId;
            });

            return _assignedPlant ? _assignedPlant.node.quantity : 0;
        },

        findSalesOrderPlant(salesOrderPlantSet, productItemNodeId) {
            // console.log(salesOrderPlantSet, productItemNodeId)

            let _assignedPlant = salesOrderPlantSet.edges.find(item => {
                return item.node.productItem.id == productItemNodeId;
            });

            return _assignedPlant;
        },

        sortProductItems(productItemSet) {
            const _sortedProductItemSet = JSON.parse(
                JSON.stringify(productItemSet)
            );

            return _sortedProductItemSet.edges.sort((a, b) => {
                if (a.node.priority > b.node.priority) {
                    return 1;
                }
                if (a.node.priority < b.node.priority) {
                    return -1;
                }
                return 0;
            });
        },

        calculateStaticProductNumPlants(productItemSet) {
            return productItemSet.edges
                .map(item => item.node.numPlants)
                .reduce(
                    (accumulator, currentValue) => accumulator + currentValue,
                    0
                );
        },

        refetchOrderDetail() {
            this.$apollo.queries.salesOrder.refresh();
        },

        updateSalesOrderPrintStatus(isPrinted) {
            // if same, don't sent to backend
            if (isPrinted == this.salesOrder.isPrinted) {
                return;
            }

            this.isSavingOrderPrintStatus = true;

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation updateSalesOrderPrintStatus(
                            $input: UpdateSalesOrderPrintStatusInput!
                        ) {
                            updateSalesOrderPrintStatus(input: $input) {
                                salesOrder {
                                    id
                                    isPrinted
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            id: this.salesOrder.id,
                            isPrinted: isPrinted
                        }
                    }
                })
                .then(response => {
                    // Result
                    // console.log(response.data);
                    // console.log(response.data.updateSalesOrderNote.salesOrder.note);

                    this.salesOrder.isPrinted =
                        response.data.updateSalesOrderPrintStatus.salesOrder.isPrinted;
                })
                .catch(error => {
                    // Error
                    console.error(error);
                    // We restore the initial user input
                    // this.newTag = newTag;
                })
                .finally(() => {
                    this.isSavingOrderPrintStatus = false;
                });
        },

        saveOrderNote(note) {
            this.isSavingOrderNote = true;

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation updateSalesOrderNote(
                            $input: UpdateSalesOrderNoteInput!
                        ) {
                            updateSalesOrderNote(input: $input) {
                                salesOrder {
                                    id
                                    note
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            id: this.salesOrder.id,
                            note: note
                        }
                    }
                })
                .then(response => {
                    // Result
                    // console.log(response.data);
                    // console.log(response.data.updateSalesOrderNote.salesOrder.note);

                    this.salesOrder.note =
                        response.data.updateSalesOrderNote.salesOrder.note;
                    this.isEditingOrderNote = false;
                })
                .catch(error => {
                    // Error
                    console.error(error);
                    // We restore the initial user input
                    // this.newTag = newTag;
                })
                .finally(() => {
                    this.isSavingOrderNote = false;
                });
        },

        saveOrderProductNote(orderItem) {
            this.$set(orderItem, "isSavingOrderProductNote", true);

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation updateSalesOrderProductNote(
                            $input: UpdateSalesOrderProductNoteInput!
                        ) {
                            updateSalesOrderProductNote(input: $input) {
                                salesOrderProduct {
                                    id
                                    note
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            id: orderItem.id,
                            note: orderItem.note
                        }
                    }
                })
                .then(() => {
                    // Result
                    // console.log(response.data);
                    // console.log(response.data.updateSalesOrderNote.salesOrder.note);

                    // orderItem.note = JSON.parse(
                    //     JSON.stringify(
                    //         response.data.updateSalesOrderProductNote
                    //             .salesOrderProduct.note
                    //     )
                    // );

                    // show snackbar
                    const payload = {
                        color: "success",
                        message: `Note saved!`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                    // orderItem.isEditingOrderProductNote = false;
                })
                .catch(error => {
                    // Error
                    console.error(error);

                    // show snackbar
                    const payload = {
                        color: "error",
                        message: `Save note failed! ${error}`
                    };
                    this.$store.dispatch("snackbar/showMessage", payload);
                })
                .finally(() => {
                    orderItem.isSavingOrderProductNote = false;
                });
        },

        openProductDialog(product) {
            this.selectedProductNodeId = product.id;

            this.productDialog = true;
            // this.$router.push({
            //     name: "product-detail",
            //     params: { id: product.id }
            // });
        },

        closeProductDialog() {
            this.productDialog = false;

            this.selectedProductNodeId = null;
        },

        goToProductDetail(productNodeId) {
            this.$router.push({
                name: "product-detail",
                params: { id: productNodeId }
            });
        },

        //////////////////////////////
        // dynamic product selection
        //////////////////////////////
        confirmProductPlants(salesOrderItem) {
            // console.log(salesOrderItem);

            this.isSavingProductPlants = true;

            var salesOrderProductNodeId = salesOrderItem.node.id;

            var productPlants = salesOrderItem.node.product.productItemSet.edges
                .filter(item => item.node.selected)
                .map(item => {
                    return {
                        productItemNodeId: item.node.id,
                        quantity:
                            salesOrderItem.node.quantity * item.node.numPlants
                    };
                });

            // console.log(salesOrderProductNodeId);
            // console.log(productPlants);

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation updateSaelsOrderProductPlants(
                            $input: UpdateSalesOrderProductPlantsInput!
                        ) {
                            updateSaelsOrderProductPlants(input: $input) {
                                salesOrderProduct {
                                    id
                                    quantity
                                    quantityPerContainer
                                    externalContainer
                                    isPlantsConfirmed
                                    isManualConfig
                                    isReady
                                    salesOrderPlantSet {
                                        totalCount
                                        edges {
                                            node {
                                                id
                                                productItem {
                                                    id
                                                }
                                                quantity
                                                numPlantsPerProduct
                                                isReady
                                                plant {
                                                    id
                                                    name
                                                }
                                                potSize {
                                                    id
                                                    potSizeCm
                                                }
                                                color {
                                                    id
                                                    color
                                                }
                                                environment {
                                                    id
                                                    environment
                                                }
                                                tag {
                                                    id
                                                    tag
                                                }
                                            }
                                        }
                                    }
                                    product {
                                        id
                                        isDynamic
                                        name
                                        externalCode
                                        externalPotsize
                                        externalHeight
                                        externalCover
                                        externalLabel
                                        externalSticker
                                        isPlantsConfigured
                                        numColorsPerUnitMax
                                        numColorsPerUnitMin
                                        numMixPlantsPerUnitMin
                                        numMixPlantsPerUnitMax
                                        productItemSet {
                                            totalCount
                                            edges {
                                                node {
                                                    id
                                                    priority
                                                    numPlants
                                                    plant {
                                                        id
                                                        name
                                                    }
                                                    potSize {
                                                        id
                                                        potSizeCm
                                                    }
                                                    color {
                                                        id
                                                        color
                                                    }
                                                    tag {
                                                        id
                                                        tag
                                                    }
                                                    environment {
                                                        id
                                                        environment
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            salesOrderProductNodeId: salesOrderProductNodeId,
                            productPlants: productPlants
                        }
                    }
                })
                .then(response => {
                    // Result
                    // console.log(response.data);
                    salesOrderItem.node = response.data.salesOrderProduct;
                })
                .catch(error => {
                    // Error
                    console.error(error);
                    // We restore the initial user input
                    // this.newTag = newTag;
                })
                .finally(() => {
                    this.isSavingProductPlants = false;
                });
        },

        openOrderCartPreviewListDialog(orderCartNodeId) {
            this.selectedOrderCartNodeIdList = [];
            this.selectedOrderCartNodeIdList.push(orderCartNodeId);
            // this.selectedOrderCartNodeId = orderCartNodeId;
            this.orderCartPreviewListDialog = true;
        },

        closeOrderCartPreviewListDialog() {
            this.orderCartPreviewListDialog = false;
            // this.selectedOrderCartNodeId = null;
            this.selectedOrderCartNodeIdList = [];
        },

        printAllOrderCarts() {
            this.selectedOrderCartNodeIdList = [];
            this.selectedOrderCartNodeIdList = this.orderCarts.map(
                item => item.node.id
            );
            this.orderCartPreviewListDialog = true;
        },

        //////////////////////////////
        // sales order product editor
        //////////////////////////////
        openSalesOrderProductEditorDialog(salesOrderProduct) {
            this.selectedSalesOrderProduct = salesOrderProduct;
            this.orderProductEditorDialog = true;
        },

        closeSalesOrderProductEditorDialog() {
            this.orderProductEditorDialog = false;
            this.selectedSalesOrderProduct = {};
        },

        onSalesOrderProductEditorDialogSaved() {
            this.orderProductEditorDialog = false;
            this.selectedSalesOrderProduct = {};
            this.refetchOrderDetail();
        },

        //////////////////////////////
        // complete order
        //////////////////////////////
        completeOrder(salesOrder) {
            // console.log(salesOrderItem);

            this.isCompletingSalesOrder = true;

            // console.log(salesOrderProductNodeId);
            // console.log(productPlants);

            this.$apollo
                .mutate({
                    // Query
                    mutation: gql`
                        mutation completeSalesOrder(
                            $input: CompleteSalesOrderInput!
                        ) {
                            completeSalesOrder(input: $input) {
                                salesOrder {
                                    id
                                    note
                                    customer {
                                        id
                                        name
                                    }
                                    orderCreatedDate
                                    orderStatus
                                    orderStatusDate
                                    salesOrderNo
                                    deliverDatePlanned
                                    totalProductQuantity
                                    closedDate
                                    closedBy
                                    salesOrderCartSet {
                                        totalCount
                                        edges {
                                            node {
                                                id
                                                cartSeq
                                                externalSalesOrderCartId
                                            }
                                        }
                                    }
                                    salesOrderProductSet {
                                        edges {
                                            node {
                                                id
                                                quantity
                                                quantityPerContainer
                                                externalContainer
                                                isPlantsConfirmed
                                                isManualConfig
                                                isReady
                                                salesOrderPlantSet {
                                                    totalCount
                                                    edges {
                                                        node {
                                                            id
                                                            productItem {
                                                                id
                                                            }
                                                            quantity
                                                            numPlantsPerProduct
                                                            isReady
                                                            plant {
                                                                id
                                                                name
                                                            }
                                                            potSize {
                                                                id
                                                                potSizeCm
                                                            }
                                                            color {
                                                                id
                                                                color
                                                            }
                                                            environment {
                                                                id
                                                                environment
                                                            }
                                                            tag {
                                                                id
                                                                tag
                                                            }
                                                        }
                                                    }
                                                }
                                                product {
                                                    id
                                                    isDynamic
                                                    name
                                                    externalCode
                                                    externalPotsize
                                                    externalHeight
                                                    externalCover
                                                    externalLabel
                                                    externalSticker
                                                    isPlantsConfigured
                                                    numColorsPerUnitMax
                                                    numColorsPerUnitMin
                                                    numMixPlantsPerUnitMin
                                                    numMixPlantsPerUnitMax
                                                    productItemSet {
                                                        totalCount
                                                        edges {
                                                            node {
                                                                id
                                                                priority
                                                                numPlants
                                                                plant {
                                                                    id
                                                                    name
                                                                }
                                                                potSize {
                                                                    id
                                                                    potSizeCm
                                                                }
                                                                color {
                                                                    id
                                                                    color
                                                                }
                                                                tag {
                                                                    id
                                                                    tag
                                                                }
                                                                environment {
                                                                    id
                                                                    environment
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    // Parameters
                    variables: {
                        input: {
                            id: salesOrder.id
                        }
                    }
                })
                .then(response => {
                    // Result
                    // console.log(response);

                    salesOrder = response.data.salesOrder;
                })
                .catch(error => {
                    // Error
                    console.error(error);
                    // We restore the initial user input
                    // this.newTag = newTag;
                })
                .finally(() => {
                    this.isCompletingSalesOrder = false;
                });
        },

        countSalesOrderItemSelectedPlants(salesOrderItem) {
            return salesOrderItem.node.product.productItemSet.edges.filter(
                item => item.node.selected
            ).length;
        },

        onEditOrderItemPlants(salesOrderItem) {
            salesOrderItem.node.isPlantsConfirmed = false;
            salesOrderItem.node.isEditing = true;
        },

        onCancelEditOrderItemPlants(salesOrderItem) {
            salesOrderItem.node.isPlantsConfirmed = true;
            salesOrderItem.node.isEditing = false;
        },

        /// Sales Order Plant Growth Task List Dialog
        openSalesOrderPlantGrowthTaskList(
            salesOrderPlantSet,
            productItemNodeId
        ) {
            // console.log(productItemNodeId);

            const _salesOrderPlant = this.findSalesOrderPlant(
                salesOrderPlantSet,
                productItemNodeId
            );

            if (_salesOrderPlant) {
                this.selectedSalesOrderPlantNodeId = _salesOrderPlant.node.id;
                this.salesOrderPlantGrowthTaskListDialog = true;
            }
        },

        closeSalesOrderPlantGrowthTaskListDialog() {
            this.selectedSalesOrderPlantNodeId = null;
            this.salesOrderPlantGrowthTaskListDialog = false;
        }
    }
};
</script>

<style scoped>
.wrap-text {
    white-space: normal;
}
</style>